import { useEffect, useState } from 'react'
import { api, ApiError, type OrgSettings } from '../lib/api'

/** Minutes on screen, seconds in the database. Nobody thinks about response
 *  time in seconds, and nobody should have to multiply in their head. */
const toMinutes = (s: number | null) => (s == null ? '' : String(Math.round(s / 60)))
const toSeconds = (m: string) => Math.round(Number(m) * 60)

type TimerDef = {
  key: 'unanswered_after_seconds' | 'stalled_after_seconds' | 'relocation_ack_seconds'
  title: string
  question: string
  detail: string
}

const TIMERS: TimerDef[] = [
  {
    key: 'unanswered_after_seconds',
    title: 'Nobody has taken it',
    question: 'A call goes out and no one accepts within…',
    detail:
      'Goes red on the dispatcher’s screen. Optionally re-broadcasts instead — see below.',
  },
  {
    key: 'stalled_after_seconds',
    title: 'Taken, but stalled',
    question: 'Someone accepted, then nothing — no on-scene report within…',
    detail:
      'Flags to the dispatcher, so a silent call does not sit unnoticed. Never re-broadcasts: it already has a responder.',
  },
  {
    key: 'relocation_ack_seconds',
    title: 'Relocation unacknowledged',
    question: 'The call moved and the accepted responder has not confirmed within…',
    detail:
      'Dispatcher-facing, deliberately. Somebody already driving to the old address may not be looking at his phone, so this is human follow-up rather than automation.',
  },
]

export function Operations() {
  const [settings, setSettings] = useState<OrgSettings | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const s = await api.orgSettings()
      setSettings(s)
      setDraft({
        unanswered_after_seconds: toMinutes(s.unanswered_after_seconds),
        stalled_after_seconds: toMinutes(s.stalled_after_seconds),
        relocation_ack_seconds: toMinutes(s.relocation_ack_seconds),
        place_match_radius_metres: String(s.place_match_radius_metres ?? ''),
      })
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load settings.')
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function save(patch: Record<string, unknown>) {
    setBusy(true)
    setSaved(false)
    try {
      const saved = await api.orgSettingsUpdate(patch)
      setSettings(saved)
      setSaved(true)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  function saveTimers() {
    const patch: Record<string, unknown> = {}
    for (const t of TIMERS) {
      const v = draft[t.key]
      // An emptied box means OFF, and is sent as such. It used to be skipped,
      // so clearing a timer silently left the old value in place -- the box
      // came back filled in on the next load and the operator was told nothing.
      if (v === '') patch[t.key] = null
      else if (Number.isFinite(Number(v))) patch[t.key] = toSeconds(v)
    }
    const radius = draft.place_match_radius_metres
    if (radius !== '' && Number.isFinite(Number(radius)))
      patch.place_match_radius_metres = Number(radius)
    save(patch)
  }

  if (!settings) {
    return (
      <div className="workspace">
        {error && <div className="notice">{error}</div>}
      </div>
    )
  }

  return (
    <div className="workspace">
      {error && <div className="notice">{error}</div>}

      <div className="card">
        <h2>Escalation timers</h2>
        <p className="blurb">
          Three independent timers. All of them flag on the dispatcher’s screen
          — the system notices, a human follows up. Set these to what your
          organization actually considers late; the values below started as
          placeholders.
        </p>

        {TIMERS.map((t) => (
          <div className="timer" key={t.key}>
            <div className="timer-head">{t.title}</div>
            <div className="timer-q">{t.question}</div>
            <div className="timer-in">
              <input
                type="number"
                min={1}
                max={120}
                value={draft[t.key] ?? ''}
                onChange={(e) => setDraft({ ...draft, [t.key]: e.target.value })}
              />
              <span className="unit">minutes</span>
            </div>
            <div className="timer-detail">
              {t.detail}
              {' '}
              {draft[t.key] === ''
                ? <strong>Off — nothing is flagged for this.</strong>
                : 'Leave it empty to switch this flag off entirely.'}
            </div>
          </div>
        ))}

        <label className="check spaced">
          <input
            type="checkbox"
            checked={!!settings.unanswered_rebroadcasts}
            onChange={(e) => save({ unanswered_rebroadcasts: e.target.checked })}
          />
          Re-broadcast an unanswered call instead of only turning it red
        </label>

        <div className="field spaced">
          <label>
            Place-match radius — how close two pinned points must be to count as
            the same place
          </label>
          <div className="timer-in">
            <input
              type="number"
              min={1}
              max={5000}
              value={draft.place_match_radius_metres ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, place_match_radius_metres: e.target.value })
              }
            />
            <span className="unit">metres</span>
          </div>
          <p className="footnote">
            A dense street and open countryside disagree about what counts as
            the same spot, which is why this is a setting rather than a fixed
            number.
          </p>
        </div>

        <div className="btnrow">
          <button className="primary compact" onClick={saveTimers} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && <span className="testresult ok">Saved.</span>}
        </div>
      </div>

      {/* REOPENING A CLOSED CALL (072): how long a closed call stays under
          Closed on the dispatch screen with a Reopen button. Saved on choice. */}
      <div className="card">
        <h2>Reopening a closed call</h2>
        <div className="field">
          <label htmlFor="reopen-window">
            After a call is closed, any dispatcher can reopen it for
          </label>
          <select id="reopen-window" value={String(settings.reopen_window_minutes ?? 60)}
                  onChange={(e) => save({ reopen_window_minutes: Number(e.target.value) })}>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="120">2 hours</option>
            <option value="240">4 hours</option>
            <option value="0">Never — no reopening</option>
            {![30, 60, 120, 240, 0].includes(settings.reopen_window_minutes ?? 60) && (
              <option value={String(settings.reopen_window_minutes)}>
                {settings.reopen_window_minutes} minutes
              </option>
            )}
          </select>
          <p className="footnote">
            Closed calls show under the Closed tile on the dispatch screen for this
            long. After that they are only under Reports / History, Past calls.
          </p>
        </div>
      </div>

      {/* SIGNING OUT (075): how long a screen nobody uses stays signed in.
          Every real use moves the end on; the screen's own refreshing does
          not. Saved on choice. */}
      <div className="card">
        <h2>Signing out</h2>
        <div className="field">
          <label htmlFor="signout-idle">Sign a screen out after</label>
          <select id="signout-idle" value={String(settings.signout_idle_minutes ?? 720)}
                  onChange={(e) => save({ signout_idle_minutes: Number(e.target.value) })}>
            <option value="60">1 hour</option>
            <option value="240">4 hours</option>
            <option value="480">8 hours</option>
            <option value="720">12 hours</option>
            <option value="1440">24 hours</option>
            <option value="4320">3 days</option>
            <option value="10080">7 days</option>
            {![60, 240, 480, 720, 1440, 4320, 10080].includes(settings.signout_idle_minutes ?? 720) && (
              <option value={String(settings.signout_idle_minutes)}>
                {settings.signout_idle_minutes} minutes
              </option>
            )}
          </select>
          <p className="footnote">
            with nobody using it. A key, a click, a tap or the mouse counts as using it;
            the screen refreshing itself does not. A screen in use is never signed out,
            and a warning comes five minutes before.
          </p>
        </div>
      </div>

      {/* THE HEADER AND FOOTER LIVED HERE until 18 Sep 2026. They are on the
          Messages tab now, beside what the member who accepts is sent, so
          everything a member's phone receives is set in one place. */}
      <div className="card">
        <h2>Routing</h2>
        <label className="check">
          <input
            type="checkbox"
            checked={!!settings.departments_enabled}
            onChange={(e) => save({ departments_enabled: e.target.checked })}
          />
          Route calls by department
        </label>
        <p className="footnote">
          Off means every call goes to everyone, which is how most
          organizations start. Assign departments to your call natures first,
          then switch this on — no rebuild, no migration.
        </p>
      </div>

      <div className="card">
        <h2>Retention</h2>
        <p className="blurb">
          What this CRM keeps. Blank means keep indefinitely.
        </p>

        <label className="check">
          <input
            type="checkbox"
            checked={!!settings.copy_fleet_recordings}
            onChange={(e) => save({ copy_fleet_recordings: e.target.checked })}
          />
          Keep our own copy of radio audio
        </label>
        <p className="footnote">
          The fleet server deletes recordings after ninety days. A
          first-responder record outlives that comfortably, so a copy is taken
          rather than a reference kept. Turning this off means an incident
          older than ninety days has no radio audio at all.
        </p>
      </div>
    </div>
  )
}
